import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWallet, FaGift, FaHistory, FaTimes, FaCheckCircle, FaClock } from 'react-icons/fa';
import { useStyleCredits } from '../context/StyleCreditsContext';
import './StyleCreditsWallet.css';

const StyleCreditsWallet = ({ onClose, onUseCredits, orderTotal = 0 }) => {
  const { credits, getMaxUsableCredits, hasEnoughCredits } = useStyleCredits();
  const [creditsToUse, setCreditsToUse] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  const maxUsableCredits = getMaxUsableCredits(orderTotal);
  const canUseCredits = credits.availableCredits > 0 && orderTotal > 0;
  const hasCredits = credits.availableCredits > 0;

  const handleCreditsChange = (value) => {
    const numValue = parseInt(value) || 0;
    const cappedValue = Math.min(numValue, maxUsableCredits);
    setCreditsToUse(cappedValue);
  };

  const handleUseCredits = () => {
    if (creditsToUse > 0 && creditsToUse <= maxUsableCredits) {
      onUseCredits(creditsToUse);
    }
  };

  const formatCredits = (amount) => {
    // Handle NaN, undefined, null, or non-numeric values
    const numericAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return `₹${numericAmount.toLocaleString()}`;
  };

  const getCreditsPercentage = () => {
    if (orderTotal === 0) return 0;
    return Math.round((maxUsableCredits / orderTotal) * 100);
  };

  return (
    <motion.div 
      className="style-credits-wallet"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Header */}
      <div className="credits-wallet-header">
        <div className="credits-wallet-title">
          <FaWallet className="credits-wallet-icon" />
          <h3>Style Credits Wallet</h3>
        </div>
        <button 
          className="credits-wallet-close"
          onClick={onClose}
          aria-label="Close wallet"
        >
          <FaTimes />
        </button>
      </div>

      {/* Credits Balance */}
      <div className="credits-balance-section">
        <div className="credits-balance-card">
          <div className="credits-balance-main">
            <span className="credits-balance-label">Available Credits</span>
            <span className="credits-balance-amount">
              {formatCredits(credits.availableCredits)}
            </span>
          </div>
          <div className="credits-balance-details">
            <div className="credits-detail-item">
              <span>Total Earned</span>
              <span>{formatCredits(credits.totalCredits)}</span>
            </div>
            <div className="credits-detail-item">
              <span>Used</span>
              <span>{formatCredits(credits.usedCredits)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Credits Usage Section */}
      {canUseCredits && (
        <div className="credits-usage-section">
          <div className="credits-usage-header">
            <h4>Use Credits for This Order</h4>
            <span className="credits-usage-limit">
              Max: {formatCredits(maxUsableCredits)} ({getCreditsPercentage()}% of order)
            </span>
          </div>
          
          <div className="credits-input-section">
            <div className="credits-input-wrapper">
              <span className="credits-currency">₹</span>
              <input
                type="number"
                value={creditsToUse}
                onChange={(e) => handleCreditsChange(e.target.value)}
                placeholder="0"
                min="0"
                max={maxUsableCredits}
                className="credits-input"
              />
            </div>
            
            <div className="credits-quick-options">
              <button 
                className="credits-quick-btn"
                onClick={() => setCreditsToUse(Math.floor(maxUsableCredits * 0.25))}
              >
                25%
              </button>
              <button 
                className="credits-quick-btn"
                onClick={() => setCreditsToUse(Math.floor(maxUsableCredits * 0.5))}
              >
                50%
              </button>
              <button 
                className="credits-quick-btn"
                onClick={() => setCreditsToUse(Math.floor(maxUsableCredits * 0.75))}
              >
                75%
              </button>
              <button 
                className="credits-quick-btn"
                onClick={() => setCreditsToUse(maxUsableCredits)}
              >
                Max
              </button>
            </div>
          </div>

          {creditsToUse > 0 && (
            <div className="credits-savings-preview">
              <div className="savings-item">
                <span>Order Total</span>
                <span>{formatCredits(orderTotal)}</span>
              </div>
              <div className="savings-item credits-discount">
                <span>Credits Applied</span>
                <span>-{formatCredits(creditsToUse)}</span>
              </div>
              <div className="savings-item savings-total">
                <span>New Total</span>
                <span>{formatCredits(orderTotal - creditsToUse)}</span>
              </div>
            </div>
          )}

          <button 
            className="credits-apply-btn"
            onClick={handleUseCredits}
            disabled={creditsToUse <= 0 || creditsToUse > maxUsableCredits}
          >
            <FaCheckCircle />
            Apply {formatCredits(creditsToUse)} Credits
          </button>
        </div>
      )}

      {/* No Credits Message */}
      {!hasCredits && (
        <div className="credits-no-credits">
          <FaGift className="no-credits-icon" />
          <h4>No Credits Available</h4>
          <p>Complete an order to earn Style Credits!</p>
        </div>
      )}


      {/* Credits History Toggle */}
      <div className="credits-history-section">
        <button 
          className="credits-history-btn"
          onClick={() => setShowHistory(!showHistory)}
        >
          <FaHistory />
          {showHistory ? 'Hide' : 'Show'} Credits History
        </button>
        
        <AnimatePresence>
          {showHistory && (
            <motion.div 
              className="credits-history-list"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {credits.creditsHistory.length === 0 ? (
                <div className="credits-history-empty">
                  <FaClock />
                  <p>No credits history yet</p>
                </div>
              ) : (
                credits.creditsHistory.map((entry, index) => (
                  <div key={index} className={`credits-history-item ${entry.type}`}>
                    <div className="history-item-icon">
                      {entry.type === 'earned' ? <FaGift /> : <FaWallet />}
                    </div>
                    <div className="history-item-details">
                      <span className="history-item-description">
                        {entry.description}
                      </span>
                      <span className="history-item-date">
                        {entry.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                      </span>
                    </div>
                    <div className={`history-item-amount ${entry.type}`}>
                      {entry.type === 'earned' ? '+' : '-'}{formatCredits(entry.amount)}
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="credits-wallet-footer">
        <p className="credits-info-text">
          Earn 5% back as Style Credits on every order. Credits expire after 6 months.
        </p>
      </div>
    </motion.div>
  );
};

export default StyleCreditsWallet;

